import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { useProject } from "../context/ProjectContext.jsx";
import {
  getOrgConnectingAuditRequest,
  getOrgConnectingAuditSubhead,
  getOrgConnectingDisplayTarget,
  isOrySelfHostedProject,
  orgConnectingRequiresOktaSession,
  resolveProjectProviderId,
} from "../utils/projectProvider.js";
import OryAttestationGate from "../components/ory/OryAttestationGate.jsx";

const OKTA_CHECKLIST = [
  "Token verified",
  "Reading policies...",
  "Checking authenticators...",
  "Reviewing applications...",
  "Auditing admin accounts...",
  "Inspecting group rules...",
  "Analyzing password policies...",
  "Checking ThreatInsight...",
  "Reviewing network zones...",
  "Compiling findings...",
];

const AUTH0_CHECKLIST = [
  "Validating Auth0 tenant domain",
  "Checking Management API readiness",
  "Reading tenant settings",
  "Reviewing applications",
  "Sampling roles and permissions",
  "Inspecting connections",
  "Reading attack protection settings",
  "Checking log streams",
  "Evaluating compliance scope",
  "Compiling findings...",
];

const ORY_NETWORK_CHECKLIST = [
  "Validating Ory Project API key",
  "Collecting Kratos identities",
  "Reading OAuth2 clients",
  "Fetching identity schemas",
  "Sampling sessions",
  "Probing project configuration",
  "Normalizing posture surfaces",
  "Evaluating security rules",
  "Mapping framework citations",
  "Compiling findings...",
];

const ORY_SELF_HOSTED_CHECKLIST = [
  "Validating Kratos admin API",
  "Collecting identities and schemas",
  "Probing Hydra admin (if connected)",
  "Reading deployment configuration",
  "Applying attested module scope",
  "Normalizing posture surfaces",
  "Evaluating security rules",
  "Mapping framework citations",
  "Compiling findings...",
];

const ENTRA_CHECKLIST = [
  "Validating Microsoft Graph access",
  "Reading Conditional Access policies",
  "Collecting authentication methods",
  "Reviewing enterprise applications",
  "Auditing directory role assignments",
  "Inspecting groups and dynamic rules",
  "Sampling sign-in and audit logs",
  "Reading named locations",
  "Checking Identity Protection signals",
  "Compiling findings...",
];

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: "14px",
        height: "14px",
        border: "2px solid var(--border-bright)",
        borderTopColor: "var(--accent)",
        borderRadius: "50%",
        animation: "idwiz-spin 0.7s linear infinite",
      }}
    />
  );
}

export default function OrgConnecting() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { orgUrl: sessionOrg, apiToken } = useOktaSession();
  const { project, isLoading } = useProject();
  const providerId = resolveProjectProviderId(project);
  const orySelfHosted = isOrySelfHostedProject(project);
  const CHECKLIST =
    providerId === "ory"
      ? orySelfHosted
        ? ORY_SELF_HOSTED_CHECKLIST
        : ORY_NETWORK_CHECKLIST
      : providerId === "entra"
        ? ENTRA_CHECKLIST
        : providerId === "auth0"
          ? AUTH0_CHECKLIST
          : providerId === "okta"
            ? OKTA_CHECKLIST
            : OKTA_CHECKLIST;

  const [stepPhase, setStepPhase] = useState(() => CHECKLIST.map(() => "hidden"));
  const [error, setError] = useState(null);
  const [attestationReady, setAttestationReady] = useState(false);
  const [attestationRequired, setAttestationRequired] = useState(false);
  const timersRef = useRef([]);
  const auditResultRef = useRef(null);
  const finishedRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  }, []);

  const fastForwardDone = useCallback(() => {
    setStepPhase(CHECKLIST.map(() => "done"));
  }, [CHECKLIST]);

  useEffect(() => {
    setStepPhase(CHECKLIST.map(() => "hidden"));
  }, [CHECKLIST.length, providerId]);

  useEffect(() => {
    setStepPhase(CHECKLIST.map(() => "hidden"));
  }, [CHECKLIST.length, providerId]);

  useEffect(() => {
    let cancelled = false;
    if (!projectId || !project) {
      return undefined;
    }
    if (!isOrySelfHostedProject(project)) {
      setAttestationRequired(false);
      setAttestationReady(true);
      return undefined;
    }
    setAttestationReady(false);
    (async () => {
      try {
        const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}/ory-attestation`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!data.requires_attestation) {
          setAttestationRequired(false);
          setAttestationReady(true);
          return;
        }
        setAttestationRequired(true);
        setAttestationReady(Boolean(data.attestation_present && !data.attestation_stale));
      } catch {
        if (!cancelled) {
          setAttestationRequired(true);
          setAttestationReady(false);
          setError("Could not load attestation status — confirm scope before running the assessment.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, project]);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setError("Missing project.");
      return undefined;
    }
    if (isLoading || !project) return undefined;
    if (!attestationReady) return undefined;

    if (orgConnectingRequiresOktaSession(project)) {
      const orgUrl = sessionOrg?.trim();
      const tok = apiToken?.trim();
      if (!orgUrl || !tok) {
        setError("Missing session — return to the dashboard and connect again.");
        return undefined;
      }
    }

    const schedule = (fn, ms) => {
      const id = window.setTimeout(fn, ms);
      timersRef.current.push(id);
    };

    const runAnimation = () => {
      for (let i = 0; i < CHECKLIST.length; i += 1) {
        const appear = i * 120;
        schedule(() => {
          setStepPhase((prev) => {
            const next = [...prev];
            if (next[i] === "hidden") next[i] = "spin";
            return next;
          });
        }, appear);
      }
      const markDone = (idx, t) => {
        schedule(() => {
          setStepPhase((prev) => {
            const next = [...prev];
            if (next[idx] === "spin") next[idx] = "done";
            return next;
          });
        }, t);
      };
      for (let i = 0; i < 3; i += 1) markDone(i, 800 + i * 120);
      for (let i = 3; i < 6; i += 1) markDone(i, 1800 + (i - 3) * 120);
      for (let i = 6; i < CHECKLIST.length; i += 1) markDone(i, 2800 + (i - 6) * 100);
    };

    runAnimation();

    const runAudit = async () => {
      try {
        const { url, body } = getOrgConnectingAuditRequest(project, {
          projectId,
          orgUrl: sessionOrg,
          apiToken,
        });
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (
            res.status === 428 &&
            (data.code === "ATTESTATION_REQUIRED" || data.code === "ATTESTATION_STALE")
          ) {
            setAttestationRequired(true);
            setAttestationReady(false);
            setError(null);
            return;
          }
          throw new Error(data.error || "Org audit failed");
        }
        auditResultRef.current = data;
      } catch (e) {
        auditResultRef.current = { error: e?.message || "Audit failed" };
      }
    };

    runAudit();

    const poll = window.setInterval(() => {
      if (finishedRef.current || cancelled) return;
      const r = auditResultRef.current;
      if (!r) return;
      finishedRef.current = true;
      clearInterval(poll);
      clearTimers();
      if (r.error) {
        if (!cancelled) setError(r.error);
        return;
      }
      fastForwardDone();
      const audit = r.audit;
      schedule(() => {
        if (cancelled) return;
        navigate(`/project/${encodeURIComponent(projectId)}`, {
          replace: true,
          state: {
            audit,
            isGreenfield: Boolean(audit?.isGreenfield),
          },
        });
      }, 350);
    }, 120);

    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimers();
    };
  }, [
    apiToken,
    clearTimers,
    fastForwardDone,
    project,
    providerId,
    isLoading,
    navigate,
    projectId,
    sessionOrg,
    CHECKLIST.length,
    attestationReady,
  ]);

  const displayTarget = getOrgConnectingDisplayTarget(project, sessionOrg);
  const auditSubhead = getOrgConnectingAuditSubhead(project);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-base)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "48px 24px",
      }}
    >
      <span
        style={{
          fontSize: "13px",
          fontWeight: 700,
          letterSpacing: "0.15em",
          color: "var(--accent)",
          marginBottom: "32px",
        }}
      >
        IDWIZ
      </span>
      <p style={{ fontSize: "16px", color: "var(--text-secondary)", marginBottom: "8px" }}>
        Connecting to{" "}
        <span className="font-mono text-accent" style={{ fontSize: "14px" }}>
          {displayTarget}
        </span>
      </p>
      <p className="text-dim" style={{ fontSize: "13px", marginBottom: "32px" }}>
        {auditSubhead}
      </p>

      {attestationRequired && !attestationReady ? (
        <OryAttestationGate
          projectId={projectId}
          onComplete={() => {
            setError(null);
            setAttestationReady(true);
          }}
        />
      ) : error ? (
        <p className="text-critical" style={{ maxWidth: "420px", textAlign: "center" }}>
          {error}
        </p>
      ) : (
        <ul style={{ listStyle: "none", width: "100%", maxWidth: "440px" }}>
          {CHECKLIST.map((label, i) => {
            const phase = stepPhase[i] || "hidden";
            if (phase === "hidden") {
              return <li key={label} style={{ height: "28px", marginBottom: "8px" }} />;
            }
            return (
              <li
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "10px",
                  fontSize: "14px",
                  color: phase === "done" ? "var(--text-secondary)" : "var(--text-primary)",
                }}
              >
                <span style={{ width: "18px", display: "flex", justifyContent: "center" }}>
                  {phase === "done" ? (
                    <span style={{ color: "var(--healthy)" }} aria-hidden>
                      ✓
                    </span>
                  ) : (
                    <Spinner />
                  )}
                </span>
                <span>{label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
